'use client';

import React, { useState, useCallback } from 'react';
import Cropper, { Point, Area } from 'react-easy-crop';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import getCroppedImg from '@/doi-tac-module/lib/crop-image-utils';
import { Loader2, ZoomIn, RotateCw } from 'lucide-react';
import { useToast } from "@/doi-tac-module/hooks/use-toast";

interface AvatarCropperModalProps {
    imageSrc: string | null;
    isOpen: boolean;
    onClose: () => void;
    onCropComplete: (croppedFile: File) => void;
    t: (key: string) => string;
    aspect?: number;
}

export function AvatarCropperModal({
    imageSrc,
    isOpen,
    onClose,
    onCropComplete,
    t,
    aspect = 1
}: AvatarCropperModalProps) {
    const { toast } = useToast();
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const onCropChange = (crop: Point) => {
        setCrop(crop);
    };

    const onZoomChange = (zoom: number) => {
        setZoom(zoom);
    };

    const onCropCompleteInternal = useCallback((_: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleSave = async () => {
        if (!imageSrc || !croppedAreaPixels) return;

        setIsProcessing(true);
        try {
            console.log("Starting cropping process for image:", imageSrc?.substring(0, 50));
            const croppedBlob = await getCroppedImg(
                imageSrc,
                croppedAreaPixels,
                rotation
            );

            if (croppedBlob) {
                const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
                console.log("Created cropped File object:", { 
                    name: file.name, 
                    size: file.size, 
                    type: file.type 
                });
                onCropComplete(file);
                onClose();
            } else {
                throw new Error("Could not generate cropped blob.");
            }
        } catch (e: any) {
            console.error('Error cropping image:', e);
            let errorMessage = e.message || t('cropErrorDesc') || "Đã có lỗi xảy ra trong quá trình xử lý ảnh. Vui lòng thử lại.";
            
            // Handle common Canvas security errors (CORS)
            if (e.name === 'SecurityError' || (e.message && e.message.includes('tainted'))) {
                errorMessage = "Không thể cắt ảnh này do giới hạn bảo mật từ máy chủ chứa ảnh (CORS). Vui lòng thử tải lại trang hoặc sử dụng ảnh khác.";
            }

            toast({
                variant: "destructive",
                title: t('cropErrorTitle') || "Lỗi cắt ảnh",
                description: errorMessage,
            });
        } finally {
            setIsProcessing(false);
        }
    };

    if (!imageSrc) return null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden bg-white">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-xl font-bold">{t('cropAvatarTitle') || 'Cắt ảnh đại diện'}</DialogTitle>
                </DialogHeader>
                
                <div className="relative w-full aspect-square mt-4 bg-slate-100">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={aspect}
                        onCropChange={onCropChange}
                        onRotationChange={setRotation}
                        onCropComplete={onCropCompleteInternal}
                        onZoomChange={onZoomChange}
                        restrictPosition={false}
                        minZoom={0.2}
                    />
                </div>

                <div className="p-6 space-y-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
                            <Slider
                                value={[zoom]}
                                min={1}
                                max={3}
                                step={0.1}
                                onValueChange={(vals) => setZoom(vals[0])}
                                className="flex-grow"
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <RotateCw className="h-4 w-4 text-muted-foreground shrink-0" />
                            <Slider
                                value={[rotation]}
                                min={0}
                                max={360}
                                step={1}
                                onValueChange={(vals) => setRotation(vals[0])}
                                className="flex-grow"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={onClose} disabled={isProcessing}>
                            {t('cancel') || 'Hủy'}
                        </Button>
                        <Button onClick={handleSave} disabled={isProcessing} className="min-w-[100px]">
                            {isProcessing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                t('save') || 'Lưu'
                            )}
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}
